import { List, ListItem, Text } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import { useQuestionnaire } from "../../../features/questionnaire";
import { SecurityLocks } from "../SecurityLocks";
import { WindowLocks } from "../WindowLocks";
import { ContentTextBlock } from "./ContentTextBlock";
import { parseHtml } from "./parseHtml";

type ContentProps = {
  id?: string;
  name: string;
  text: string;
  answer: string;
  assumptionGroup: string;
  hasSensitiveData?: boolean;
  readOnly?: boolean;
  onEditQuestion?: () => void;
};

export function Content({
  id,
  name,
  text,
  answer = "",
  assumptionGroup,
  hasSensitiveData,
  readOnly,
  onEditQuestion,
}: ContentProps) {
  const { changedQuestions } = useQuestionnaire();
  const { snowplow } = useSnowplowContext();

  const { text: t, listItems } = parseHtml(text);
  const textBlocks = t.split("${answer}");

  // Determine if the answer should be rendered between multiple non-empty text segments
  const addAnswerInMiddle = textBlocks.filter(b => b !== "").length > 1;
  // Lowercase the answer only if it's in the middle of the text (has text before AND after)
  const shouldLowercaseAnswer =
    textBlocks.length === 2 && !!textBlocks[0].trim() && !!textBlocks[1].trim();
  // Determine if the question needs locks images
  const shouldShowWindowLocks = id === "sof_window_locks";
  const shouldShowDoorLocks = id === "sof_door_locks";
  // If answer has been changed, set classname
  const isChanged = changedQuestions?.includes(name);
  const classes = classNames({
    "mobius-collapsible__changed_question": isChanged,
  });

  const handleClick = () => {
    snowplow?.trigger("assumptionLinkClicked", {
      category: assumptionGroup,
      label: name,
      property: answer,
    });
    onEditQuestion?.();
  };

  return (
    <div id={name} className="mobius-collapsible__bottom-divider">
      <div className={classes}>
        <div className="mobius-collapsible__content-blocks">
          <Text key="text">
            {textBlocks.map((block, index) => {
              const shouldAddAnswer = addAnswerInMiddle && index === 1;

              return (
                <ContentTextBlock
                  key={index}
                  block={block}
                  answer={answer}
                  shouldAddAnswer={shouldAddAnswer}
                  hasSensitiveData={hasSensitiveData}
                  readOnly={readOnly}
                  onEditQuestion={handleClick}
                  shouldLowercaseAnswer={shouldLowercaseAnswer}
                />
              );
            })}
          </Text>
        </div>

        {listItems.length > 0 && (
          <List>
            {listItems.map(item => (
              <ListItem key={item}>{item}</ListItem>
            ))}
          </List>
        )}
      </div>
      {shouldShowWindowLocks && <WindowLocks />}
      {shouldShowDoorLocks && (
        <div className="mobius-collapsible__locks-spacer">
          <SecurityLocks />
        </div>
      )}
    </div>
  );
}
